"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import Lottie from "lottie-react";

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { processLottieForDarkMode } from "@/lib/utils/lottie-colors";
import { isAllowedIconLibraryPath, sanitizeSvg } from "@/lib/security/sanitize-svg";
import { ga4CtaClick } from "@/lib/analytics/ga4-ecommerce";

/** Matches the category icon circle — soft pulse, no emoji/spinner. */
function CategoryIconPlaceholder({ className }: { className?: string }) {
	return (
		<div
			className={cn(
				"absolute -left-4 -top-2 flex size-12 items-center justify-center",
				className,
			)}
			aria-hidden
		>
			<span className="size-10 rounded-full bg-secondary/20 shadow-inner motion-safe:animate-pulse dark:bg-secondary/15" />
		</div>
	);
}

type Props = {
	logo: any;
	title: string;
	description: string;
	servicesCount: number;
	_id: string;
	link: string;
};

function LottieIcon({ path, alt }: { path: string; alt: string }) {
	const [animationData, setAnimationData] = useState<any>(null);
	const [error, setError] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isDarkMode, setIsDarkMode] = useState(false);

	useEffect(() => {
		// Check if dark mode is active
		const checkDarkMode = () => {
			if (typeof window !== 'undefined') {
				return document.documentElement.classList.contains('dark') || 
				       document.documentElement.classList.contains('dark-mode');
			}
			return false;
		};

		setIsDarkMode(checkDarkMode());

		// Watch for theme changes
		const observer = new MutationObserver(() => {
			setIsDarkMode(checkDarkMode());
		});

		if (typeof window !== 'undefined') {
			observer.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['class']
			});
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		let isMounted = true;
		setLoading(true);
		setError(false);
		setAnimationData(null);

		fetch(path)
			.then(res => {
				if (!res.ok) {
					throw new Error(`File not found: ${path}`);
				}
				return res.json();
			})
			.then(data => {
				if (isMounted) {
					// Process colors for dark mode: convert black to blue, preserve green
					const processedData = isDarkMode 
						? processLottieForDarkMode(data) 
						: data;
					setAnimationData(processedData);
					setLoading(false);
				}
			})
			.catch(() => {
				if (isMounted) {
					setError(true);
					setLoading(false);
				}
			});

		return () => {
			isMounted = false;
		};
	}, [path, isDarkMode]);

	if (loading) {
		return <CategoryIconPlaceholder />;
	}

	if (error || !animationData) {
		return (
			<div
				className="absolute -left-4 -top-2 flex size-12 items-center justify-center"
				title={`${alt} — animation unavailable`}
			>
				<span className="size-10 rounded-full border border-dashed border-border/60 bg-muted/40 dark:bg-muted/25" />
			</div>
		);
	}

	return (
		<div className="absolute -left-4 -top-2 flex size-12 items-center justify-center overflow-hidden">
			<Lottie
				animationData={animationData}
				loop={true}
				autoplay={true}
				style={{ width: 48, height: 48, display: 'block' }}
			/>
		</div>
	);
}

function SvgIcon({ src, alt }: { src: string; alt: string }) {
	const [svgContent, setSvgContent] = useState<string | null>(null);
	const [isDarkMode, setIsDarkMode] = useState(false);
	const [fetchDone, setFetchDone] = useState(false);

	useEffect(() => {
		const checkDarkMode = () => {
			if (typeof window !== 'undefined') {
				return document.documentElement.classList.contains('dark') || 
				       document.documentElement.classList.contains('dark-mode');
			}
			return false;
		};

		setIsDarkMode(checkDarkMode());

		const observer = new MutationObserver(() => {
			setIsDarkMode(checkDarkMode());
		});

		if (typeof window !== 'undefined') {
			observer.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['class']
			});
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		setFetchDone(false);
		setSvgContent(null);
		if (!isAllowedIconLibraryPath(src)) {
			setSvgContent(null);
			setFetchDone(true);
			return;
		}
		fetch(src)
			.then(res => res.text())
			.then(svg => {
				if (isDarkMode) {
					// Replace black colors with blue, preserve green
					// Handle various black color formats
					let processed = svg
						// Fill attributes with quotes
						.replace(/fill="#000000"/gi, 'fill="#07A8BC"')
						.replace(/fill="#232323"/gi, 'fill="#07A8BC"')
						.replace(/fill="#000"/gi, 'fill="#07A8BC"')
						.replace(/fill="rgb\(0,\s*0,\s*0\)"/gi, 'fill="#07A8BC"')
						.replace(/fill="rgba\(0,\s*0,\s*0[^)]*\)"/gi, 'fill="#07A8BC"')
						// Stroke attributes with quotes
						.replace(/stroke="#000000"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="#232323"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="#000"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="rgb\(0,\s*0,\s*0\)"/gi, 'stroke="#07A8BC"')
						.replace(/stroke="rgba\(0,\s*0,\s*0[^)]*\)"/gi, 'stroke="#07A8BC"')
						// Fill attributes without quotes
						.replace(/fill=#000000/gi, 'fill="#07A8BC"')
						.replace(/fill=#232323/gi, 'fill="#07A8BC"')
						.replace(/fill=#000/gi, 'fill="#07A8BC"')
						// Stroke attributes without quotes
						.replace(/stroke=#000000/gi, 'stroke="#07A8BC"')
						.replace(/stroke=#232323/gi, 'stroke="#07A8BC"')
						.replace(/stroke=#000/gi, 'stroke="#07A8BC"')
						// Style attributes
						.replace(/fill:\s*#000000/gi, 'fill: #07A8BC')
						.replace(/fill:\s*#232323/gi, 'fill: #07A8BC')
						.replace(/fill:\s*#000/gi, 'fill: #07A8BC')
						.replace(/fill:\s*rgb\(0,\s*0,\s*0\)/gi, 'fill: #07A8BC')
						.replace(/stroke:\s*#000000/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*#232323/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*#000/gi, 'stroke: #07A8BC')
						.replace(/stroke:\s*rgb\(0,\s*0,\s*0\)/gi, 'stroke: #07A8BC')
						// Preserve green colors (make sure they stay green)
						.replace(/fill="#3FE0D0"/gi, 'fill="#3FE0D0"')
						.replace(/fill="#3fdfcf"/gi, 'fill="#3FE0D0"')
						.replace(/stroke="#3FE0D0"/gi, 'stroke="#3FE0D0"')
						.replace(/stroke="#3fdfcf"/gi, 'stroke="#3FE0D0"');
					
					// Handle hex colors in any context (but be careful not to break URLs or other hex values)
					// Only replace standalone hex colors, not ones that are part of longer strings
					processed = processed.replace(/([^#\w])#000000([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					processed = processed.replace(/([^#\w])#232323([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					processed = processed.replace(/([^#\w])#000([^0-9a-fA-F])/gi, '$1#07A8BC$2');
					
					setSvgContent(sanitizeSvg(processed));
				} else {
					setSvgContent(sanitizeSvg(svg));
				}
			})
			.catch(() => {
				setSvgContent(null);
			})
			.finally(() => {
				setFetchDone(true);
			});
	}, [src, isDarkMode]);

	if (!fetchDone) {
		return <CategoryIconPlaceholder />;
	}

	if (!svgContent) {
		return (
			<Image
				src={src}
				alt={alt}
				width={48}
				height={48}
				loading="lazy"
				unoptimized
				className="icon-library-icon absolute -left-4 -top-2 size-12 object-contain"
			/>
		);
	}

	return (
		<div 
			className="icon-library-icon absolute -left-4 -top-2 size-12 object-contain"
			dangerouslySetInnerHTML={{ __html: svgContent }}
		/>
	);
}

export default function CategoryCard({ logo, title, description, servicesCount, link }: Props) {
	const isJsonIcon = logo && typeof logo === 'string' && logo.startsWith('/icon-library') && logo.endsWith('.json');
	const isSvgIcon = logo && typeof logo === 'string' && logo.startsWith('/icon-library') && !logo.endsWith('.json');

	return (
		<Link
			href={link}
			onClick={() =>
				ga4CtaClick({
					cta_id: "home_category_card",
					cta_name: title,
					cta_location: "home_categories",
					link_url: link,
				})
			}
		>
			<Card className="flex flex-col rounded-lg border-none shadow-none transition-transform duration-300 hover:-translate-y-1 hover:scale-105">
				<CardHeader className="space-y-1.5 p-4 pb-0 sm:space-y-2 sm:p-6 sm:pb-1 md:pb-2">
					<div className="relative ml-3 size-12 rounded-full bg-accent-foreground/10 sm:ml-4 sm:size-14">
						{isJsonIcon ? (
							<LottieIcon path={logo} alt={`${title} icon`} />
						) : isSvgIcon ? (
							<SvgIcon src={logo} alt={`${title} icon`} />
						) : (
							<Image
								draggable="false"
								src={logo}
								alt={`${title} icon`}
								height={256}
								width={256}
								loading="lazy"
								sizes="(max-width: 640px) 48px, 56px"
								className="absolute -left-4 -top-2 size-12 object-contain"
								unoptimized={logo?.startsWith('http')}
							/>
						)}
					</div>
				<CardTitle className="h-8 !text-xsm2 !font-medium !leading-tight text-foreground sm:h-auto sm:!text-sm">
					{title}
				</CardTitle>
			</CardHeader>

			<CardContent className="h-14 p-4 pt-0 text-[10px] font-light leading-snug text-grey-foreground sm:h-20 sm:p-6 sm:pt-0 sm:text-xs dark:text-muted-foreground">
				<p className="line-clamp-3">{description}</p>
			</CardContent>

			<CardFooter className="p-4 pt-0 !text-xxsm !font-medium text-foreground sm:p-6 sm:pt-0 sm:!text-xs">
				{servicesCount} Services
			</CardFooter>
			</Card>
		</Link>
	);
}
